import { useLayoutEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { pushStudioRoute } from '@/lib/studio-navigation'
import { studioRouteLabel } from '@/lib/studio-route-registry'
import { useStudioLocale } from '@/i18n/use-studio-locale'
import { useStudioStore } from '@/store/studio-store'
import type { StudioRoute } from '@/store/types'

export const OPERATIONS_ROUTES = [
  'commerce', 'products', 'orders', 'revenue', 'members', 'analytics',
] as const satisfies readonly StudioRoute[]

type OperationsRoute = (typeof OPERATIONS_ROUTES)[number]

interface OperationsTabsProps {
  activeRoute: OperationsRoute
}

export function OperationsTabs({ activeRoute }: OperationsTabsProps) {
  const setStudioRoute = useStudioStore((state) => state.setStudioRoute)
  const { strings } = useStudioLocale()
  const activeButtonRef = useRef<HTMLButtonElement>(null)

  useLayoutEffect(() => {
    const activeButton = activeButtonRef.current
    if (typeof activeButton?.scrollIntoView !== 'function') return

    activeButton.scrollIntoView({
      behavior: 'auto',
      block: 'nearest',
      inline: 'center',
    })
  }, [activeRoute])

  function navigate(route: OperationsRoute) {
    setStudioRoute(route)
    pushStudioRoute(route)
  }

  return (
    <nav aria-label={strings.a11y.operations} className="flex gap-1 overflow-x-auto rounded-xl border border-border bg-card p-1 shadow-sm">
      {OPERATIONS_ROUTES.map((route) => {
        const active = route === activeRoute
        // `studioRouteLabel` is the single lookup the registry documents:
        // it prefers `labelKey` so the tab reads in the viewer's language,
        // where `studioRouteEntry(route).label` is always the English one.
        const label = studioRouteLabel(route, strings)

        return (
          <button
            key={route}
            type="button"
            ref={active ? activeButtonRef : undefined}
            onClick={() => navigate(route)}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'h-10 whitespace-nowrap rounded-lg px-4 text-sm font-medium transition-colors',
              active
                ? 'bg-foreground text-background'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {label}
          </button>
        )
      })}
    </nav>
  )
}
